<script>
import VueEditor  from '@/components/vue2-editor/VueEditor';
import FloatingLabelTextInput from '@/components/FloatingLabelTextInput'
import Button from '@/components/Button'
import FileUploadZone from '@/components/FileUploadZone'
import ScheduledPublishingTimePicker from '@/components/ScheduledPublishingTimePicker'
import DialogUtils from '@/utils/dialogUtils'
import EnvironmentUtils from '@/utils/environmentUtils'
import ActualitiesService from '@/services/actualities'
import StringUtils from '@/utils/stringUtils'
import VueUtils from '@/utils/vueUtils';
import Theme from '@/theme';

const REF_TITLE_TEXT_INPUT = 'title-text-input'
const REF_SUBTITLE_TEXT_INPUT = 'subtitle-text-input'
const REF_EDITOR = 'editor'

export default
{
    name: 'actuality-editor',

    props:
    {
        template: { default: undefined }
    },

    created ()
    {
    },

    mounted ()
    {
        (async () =>
        {
            if (typeof this.template === 'number')
            {
                await this.fillEditorFromActualityId(this.template)
            }

            this.actualityEditorPaddingBottom = this.$refs.actualityEditor.style.paddingBottom

            this.isActualityReleased = VueUtils.getObserverless(this.actualityExists && !this.isDraft && this.isPublished)
        })()
    },

    render () 
    {
        return (
            <div id='actuality-editor' ref='actualityEditor'>
                <div id='header-buttons'>
                    <Button id='back-button' type='secondary' outlined={true} text='Annuler' icon={require('@/assets/img/left-arrow.svg')} on-click={this.onBackButtonClicked} />
                    <Button id='delete-button' type='secondary' outlined={true} text='Supprimer' on-click={this.onDeleteButtonClicked} v-show={this.actualityExists} />
                </div>
                <floating-label-text-input id='title-input' ref={REF_TITLE_TEXT_INPUT} placeholder='Le titre de votre article'
                    v-model={this.titleContent} />
                <floating-label-text-input id='description-input' ref={REF_SUBTITLE_TEXT_INPUT} placeholder='Son sous-titre'
                    v-model={this.subtitleContent} />
                <vue-editor ref={REF_EDITOR} class='editor' placeholder='Vous pouvez editer son contenu ici.' v-model={this.bodyContent}
                    ref='editor' />
                <file-upload-zone ref='fileUploadZone' id='cover-upload-zone'
                    label="Choisir l'image de couverture" accept='.jpeg, .jpg, .png, .svg' max-file-count={1} />
                <scheduled-publishing-time-picker show={!this.isPublished} v-model={this.scheduledPublishingDate} />

                <div id='action-buttons-container'>
                    {
                        (!this.isActualityReleased) &&
                        (
                            <Button id='draft-button' outlined={true} text='Sauvegarder' on-click={this.onSaveAsDraftButtonClicked} />
                        )
                    }
                    <Button has-shadow={true} text={this.publishButtonText}
                        is-loading={this.isPublishing} on-click={this.onPublishButtonClicked} />
                </div>
            </div>
        )
    },

    data () 
    {
        return {
            actualityId: undefined,
            titleContent: '',
            subtitleContent: '',
            bodyContent: '',
            isDraft: undefined,
            isPublishing: false,
            actualityEditorPaddingBottom: undefined,
            isActualityReleased: undefined,
            scheduledPublishingDate: null,
            isPublished: false
        }
    },

    methods:
    {
        async fillEditorFromActualityId (id)
        {
            let fetched = await ActualitiesService.fetchActualityById(EnvironmentUtils.cityId, id, this.$store.state.user.token)
            this.$store.commit('actualities/updateActualityList', [fetched])

            let actuality = this.$store.getters.actualities.getActualityById(id)
            if (!actuality)
            {
                return console.error(`Cannot fill form out of actuality ID ${id}, actuality not found in store.`)
            }

            console.log('Filling editor out of', actuality)

            this.actualityId = id
            this.titleContent = actuality.title
            this.subtitleContent = actuality.description
            this.bodyContent = actuality.content
            this.isDraft = actuality.isDraft
            this.isPublished = actuality.isPublished
            this.scheduledPublishingDate = actuality.scheduledPublishingDate ? new Date(actuality.scheduledPublishingDate) : null
            
            if (!StringUtils.isEmptyString(actuality.imageUrl))
            {
                if (this.$refs.fileUploadZone) this.$refs.fileUploadZone.addToUploadedFiles(actuality.imageUrl)
            }
        },

        onBackButtonClicked ()
        {
            this.$router.back()
        },

        async onDeleteButtonClicked (event, button)
        {
            this.$ga.event('actuality', 'click:delete', this.$store.state.user.username, this.actualityId)

            const confirmed = await DialogUtils.waitForUserConfirmationAsync
            ({
                title: 'Voulez-vous vraiment supprimer cette actualité ?',
                description: 'La suppression sera irréversible',
            })
            if (!confirmed) return

            button.showSpinner = true
            await this.deleteActuality()
            button.showSpinner = false
        },

        async deleteActuality ()
        {
            this.$ga.event('actuality', 'delete', this.$store.state.user.username, this.actualityId)

            if (this.actualityExists)
            {
                await ActualitiesService.deleteActuality(this.actualityId, EnvironmentUtils.cityId, this.$store.state.user.token)
            }
            await this.$store.dispatch('actualities/removeActualityById', this.actualityId)
            
            this.$router.back()
        },

        async onSaveAsDraftButtonClicked (button)
        {
            this.$ga.event('actuality', 'click:save_draft', this.$store.state.user.username, this.actualityId)

            if (StringUtils.isEmptyString(this.titleInput.inputText))
            {
                this.titleInput.setHintMessage('Vous devez au moins fournir un titre pour sauvegarder un brouillon')
                this.titleInput.setErrorStyle()

                return
            }

            button.showSpinner = true

            this.isDraft = true

            let task
            if (this.actualityExists)
            {
                let originalActuality = this.$store.getters.actualities.getActualityById(this.actualityId)
                let changes = await this.createActualyFromChangedFieldsOnly(originalActuality)
                task = this.updateActuality(this.actualityId, changes)
            }
            else
            {
                let actuality = await this.createActualityFromFields()
                task = this.publishActuality(actuality)
            }

            await task
            button.showSpinner = false

            this.$router.back()
        },

        async onPublishButtonClicked (button)
        {
            this.$ga.event('actuality', 'click:publish', this.$store.state.user.username, this.actualityId)

            if (!this.checkForFieldsValidity()) return

            this.isPublishing = true
            this.isDraft = false

            let task
            if (this.actualityExists)
            {
                let originalActuality = this.$store.getters.actualities.getActualityById(this.actualityId)
                let changes = await this.createActualyFromChangedFieldsOnly(originalActuality)

                // the creation date stays the same if already published
                if (!originalActuality.isDraft) changes.date = originalActuality.date

                task = this.updateActuality(this.actualityId, changes)
            }
            else
            {
                let actuality = await this.createActualityFromFields()
                task = this.publishActuality(actuality)
            }
            
            await task
            this.isPublishing = false

            this.$router.push('/actualities/' + this.actualityId)
        },

        async publishActuality (actuality)
        {
            console.log('Publishing', actuality)

            const published = await ActualitiesService.postActuality(actuality, EnvironmentUtils.cityId, this.$store.state.user.token)
            this.actualityId = published.id
            
            this.$store.commit('actualities/updateActualityList', [published])
            
            this.$ga.event('actuality', 'create', this.$store.state.user.username, published.id)
        },

        async updateActuality (actualityId, actualityChanges)
        {
            this.$ga.event('actuality', 'update', this.$store.state.user.username, this.actualityId)
            console.log(`Updating actuality ${actualityId} with`, actualityChanges)

            const updated = await ActualitiesService.updateActuality(actualityId, actualityChanges, EnvironmentUtils.cityId, this.$store.state.user.token)
            this.$store.commit('actualities/updateActualityList', [updated])
        },

        async createActualityFromFields ()
        {
            let imageDataUrl = ''

            const filesUploaded = Object.values(this.$refs.fileUploadZone.filesUploaded)
            if (filesUploaded.length > 0)
            {
                imageDataUrl = filesUploaded[0]
            }

            let actuality =
            {
                id: this.actualityId,
                title: this.titleContent,
                description: this.subtitleContent,
                content: this.bodyContent,
                imageUrl: imageDataUrl,
                date: new Date().toISOString(),
                isDraft: this.isDraft,
                scheduledPublishingDate: this.scheduledPublishingDate,
                meta: { backgroundColor: Theme.current.backgroundColor }
            }

            return actuality
        },

        async createActualyFromChangedFieldsOnly (originalActuality)
        {
            let actuality = await this.createActualityFromFields()

            if (originalActuality.title == actuality.title) delete actuality.title
            if (originalActuality.description == actuality.description) delete actuality.description
            if (originalActuality.content == actuality.content) delete actuality.content
            if (new Date(originalActuality.scheduledPublishingDate) == new Date(actuality.scheduledPublishingDate)) delete actuality.scheduledPublishingDate

            return actuality
        },

        checkForFieldsValidity ()
        {
            let valid = true

            if (StringUtils.isEmptyString(this.titleInput.inputText))
            {
                this.titleInput.setErrorMessage('Ce titre n\'est pas valide')
                valid = false
            }
            if (StringUtils.isEmptyString(this.subtitleInput.inputText))
            {
                this.subtitleInput.setErrorMessage('Ce sous-titre n\'est pas valide')
                valid = false
            }
            if (StringUtils.isEmptyString(this.bodyContent))
            {
                this.$refs.editor.setErrorMessage('Votre article ne peut pas être vide')
                valid = false
            }
            if (this.$refs.fileUploadZone.filesUploadedCount < 1)
            {
                this.$refs.fileUploadZone.setErrorMessage('Vous devez fournir une image')
                valid = false
            }

            if (this.scheduledPublishingDate)
            {
                if (this.scheduledPublishingDate < new Date())
                {
                    this.$toast.error('La date de publication ne peut pas être dépassée')
                    valid = false
                }
            }

            return valid
        }
    },

    computed:
    {
        publishButtonText ()
        {
            if (this.actualityExists) return 'Mettre à jour'
            if (this.isScheduledPublishing) return 'Confirmer'
            return 'Publier'
        },

        actualityExists ()
        {
            return this.actualityId != undefined
        },

        titleInput ()
        {
            return this.$refs[REF_TITLE_TEXT_INPUT]
        },

        subtitleInput ()
        {
            return this.$refs[REF_SUBTITLE_TEXT_INPUT]
        },

        editor ()
        {
            return this.$refs[REF_EDITOR]
        },
    },

    components:
    {
        VueEditor,
        FloatingLabelTextInput,
        Button,
        FileUploadZone,
        ScheduledPublishingTimePicker,
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

#actuality-editor .ql-editor
{
    min-height: 15vh;

    .OnTablet({
        min-height: 25vh;
    });

    .OnDesktop({
        min-height: 20vh;
    });
}

#actuality-editor
{
    margin: 0 2vw 2vh 2vw;

    .OnTablet({
        margin-left: 4vw;
        margin-right: 4vw;
        margin-bottom: 1vh;
    });

    .OnDesktop({
        margin-left: 20vw;
        margin-right: 20vw;
    });

    #header-buttons
    {
        display: flex;
        flex-direction: row;
        margin-bottom: 2vh;

        #back-button
        {
            background-color: var(--theme-border-color-extra-light);
            border-color: transparent;
            width: 30vw;

            .OnDesktop({
                width: 8vw;
                padding: 1vh 1vw;
            });
        }

        #delete-button
        {
            width: auto;
            margin-left: auto;
            background-color: var(--theme-border-color-extra-light);
            border-color: transparent;
        }
    }

    .floating-label-text-input
    {
    }

    #title-input
    {
        margin-top: 20px;
    }

    #description-input
    {
        margin-top: 10px;
    }

    .editor
    {
        margin-top: 25px;
    }

    #cover-upload-zone
    {
        margin-top: 25px;
    }

    #action-buttons-container
    {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-top: 40px;

        .button
        {
            width: 100%;
            margin-right: 5vw;

            .OnTablet({
                margin-right: 10vw;
            });

            .OnDesktop({
                margin-right: 25vw;
            });

            &:last-child
            {
                margin-right: 0;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/views/ActualityEditor.vue